import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { Crosshair, KeyRound, Loader2, MapPinned, Plug, Save } from 'lucide-react';

import { AppContent, AppSection } from '@/components/layout/AppLayout';
import { getCurrentCoordinates, reverseGeocodeToAreaHint } from '@/lib/location';
import { ScreenBody } from '@/components/ScreenBody';
import { Button } from '@/components/shadcn-ui/Button';
import { Input } from '@/components/shadcn-ui/Input';
import { Label } from '@/components/shadcn-ui/Label';
import { useToast } from '@/components/shadcn-ui/use-toast';
import { useTaxiData } from '@/contexts/TaxiDataContext';

const TaxiSettings = () => {
	const { toast } = useToast();
	const { settings, updateSettings } = useTaxiData();
	const [claudeApiKeyInput, setClaudeApiKeyInput] = useState(settings.claudeApiKey);
	const [spreadsheetWebhookInput, setSpreadsheetWebhookInput] = useState(
		settings.spreadsheetWebhookUrl,
	);
	const [defaultAreaHintInput, setDefaultAreaHintInput] = useState(
		settings.defaultAreaHint,
	);
	const [isDetectingLocation, setIsDetectingLocation] = useState(false);

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		updateSettings({
			claudeApiKey: claudeApiKeyInput.trim(),
			spreadsheetWebhookUrl: spreadsheetWebhookInput.trim(),
			defaultAreaHint: defaultAreaHintInput.trim() || '新宿駅東口',
		});
		toast({ title: '設定を保存しました' });
	};

	const handleUseCurrentLocation = async () => {
		setIsDetectingLocation(true);
		try {
			const coordinates = await getCurrentCoordinates();
			const areaHint = await reverseGeocodeToAreaHint(coordinates);
			setDefaultAreaHintInput(areaHint);
			toast({
				title: '現在地から設定候補を取得しました',
				description: `${areaHint}（${coordinates.lat}, ${coordinates.lon}）`,
			});
		} catch (error) {
			toast({
				title: '現在地の取得に失敗しました',
				description: (error as Error).message,
				variant: 'destructive',
			});
		} finally {
			setIsDetectingLocation(false);
		}
	};

	return (
		<ScreenBody>
			<AppContent>
				<AppSection className="space-y-3">
					<div className="flex items-center justify-between">
						<h1 className="text-lg font-bold">設定</h1>
						<Link to="/input">
							<Button variant="outline" size="sm">
								入力に戻る
							</Button>
						</Link>
					</div>
					<p className="text-xs text-slate-600">
						AI提案とスプレッドシート同期の設定を管理します
					</p>

					<form className="space-y-3" onSubmit={handleSubmit}>
						<div className="space-y-1">
							<Label htmlFor="claude-api-key" className="flex items-center gap-1">
								<KeyRound className="h-4 w-4 text-slate-500" />
								Claude API Key
							</Label>
							<Input
								id="claude-api-key"
								type="password"
								placeholder="sk-ant-..."
								value={claudeApiKeyInput}
								onChange={(e) => setClaudeApiKeyInput(e.target.value)}
							/>
						</div>
						<div className="space-y-1">
							<Label htmlFor="spreadsheet-webhook" className="flex items-center gap-1">
								<Plug className="h-4 w-4 text-slate-500" />
								Spreadsheet Webhook URL
							</Label>
							<Input
								id="spreadsheet-webhook"
								placeholder="https://script.google.com/macros/s/..."
								value={spreadsheetWebhookInput}
								onChange={(e) => setSpreadsheetWebhookInput(e.target.value)}
							/>
						</div>
						<div className="space-y-1">
							<Label htmlFor="default-area-hint" className="flex items-center gap-1">
								<MapPinned className="h-4 w-4 text-slate-500" />
								デフォルト待機エリア
							</Label>
							<Button
								type="button"
								variant="outline"
								className="w-full"
								onClick={handleUseCurrentLocation}
								disabled={isDetectingLocation}
							>
								{isDetectingLocation ? (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								) : (
									<Crosshair className="mr-2 h-4 w-4" />
								)}
								現在位置から自動入力
							</Button>
							<Input
								id="default-area-hint"
								placeholder="新宿駅東口"
								value={defaultAreaHintInput}
								onChange={(e) => setDefaultAreaHintInput(e.target.value)}
							/>
						</div>

						<Button type="submit" className="w-full">
							<Save className="mr-2 h-4 w-4" />
							設定を保存
						</Button>
					</form>
				</AppSection>
			</AppContent>
		</ScreenBody>
	);
};

export default TaxiSettings;
